import React from "react";
import { motion } from "framer-motion";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import CustomInput from "Common/CustomInput/CustomInput";
import CustomSelect from "Common/CustomSelect/CustomSelect";
import SuperAccordion from "Components/SuperAccordion/SuperAccordion";
import { GENDER_OPTIONS, MARITAL_STATUS_OPTIONS, TODAY_DATE, IDENTITY_OPTIONS, relationship } from "Common/Constants";
import ssnFormatter from "Utils/SsnFormatter";
import PropTypes from "prop-types";

const Identification = ({
  ptModel,
  validationModel,
  showOnlyRequired,
  sectionsOpen,
  onToggleSection,
  handleChange,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 border rounded-lg"
    >
      <div
        className="flex items-center justify-between py-2 cursor-pointer"
        onClick={() => onToggleSection("identification")}
      >
        <h3 className="text-lg font-medium text-gray-900">Identification</h3>
        {sectionsOpen.identification ? (
          <FiChevronUp className="text-gray-500" />
        ) : (
          <FiChevronDown className="text-gray-500" />
        )}
      </div>
      <SuperAccordion show={sectionsOpen.identification}>
        <div className="mt-4 space-y-4">
          {!showOnlyRequired && (
            <div>
              <CustomInput
                toLower={true}
                onChange={handleChange}
                name="externalPatientId"
                value={ptModel.externalPatientId}
                label="External Patient ID"
                toolTip="A unique identifier for the patient from an external system"
              />
            </div>
          )}

          <div className="grid grid-cols-12 gap-4">
            <div className={`col-span-12 ${showOnlyRequired ? 'md:col-span-6' : 'md:col-span-4'}`}>
              <CustomInput
                onChange={handleChange}
                name="firstName"
                value={ptModel.firstName}
                required
                label="First Name"
                placeholder="First Name"
                toLower
              />
              {validationModel.firstName}
            </div>
            {!showOnlyRequired && (
              <div className="col-span-12 md:col-span-4">
                <CustomInput
                  onChange={handleChange}
                  name="middleName"
                  value={ptModel.middleName}
                  label="Middle Name"
                  placeholder="Middle Name"
                  toLower
                  maxlength="3"
                />
              </div>
            )}
            <div className={`col-span-12 ${showOnlyRequired ? 'md:col-span-6' : 'md:col-span-4'}`}>
              <CustomInput
                onChange={handleChange}
                name="lastName"
                value={ptModel.lastName}
                required
                label="Last Name"
                placeholder="Last Name"
                toLower
              />
              {validationModel.lastName}
            </div>
          </div>

          {showOnlyRequired ? (
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-6">
                <CustomInput
                  onChange={handleChange}
                  name="dob"
                  value={ptModel.dob}
                  required
                  label="Date of Birth"
                  min="1900-01-01"
                  max={TODAY_DATE}
                  type="date"
                />
                {validationModel.dobError}
              </div>
              <div className="col-span-12 md:col-span-6">
                <CustomSelect
                  onChange={handleChange}
                  name="gender"
                  required
                  value={ptModel.gender}
                  label="Gender"
                >
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </CustomSelect>
                {validationModel.genderError}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6">
                  <CustomInput
                    onChange={handleChange}
                    name="dob"
                    value={ptModel.dob}
                    required
                    label="Date of Birth"
                    min="1900-01-01"
                    max={TODAY_DATE}
                    type="date"
                  />
                  {validationModel.dobError}
                </div>
                <div className="col-span-12 md:col-span-6">
                  <CustomInput
                    onChange={handleChange}
                    maxlength="9"
                    isNumeric
                    name="ssn"
                    value={ssnFormatter(ptModel.ssn)}
                    label="SSN"
                    placeholder="XXX-XX-XXXX"
                    toolTip="Social Security Number - Format: XXX-XX-XXXX"
                  />
                  {validationModel.ssnError}
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6">
                  <CustomSelect
                    onChange={handleChange}
                    name="gender"
                    required
                    value={ptModel.gender}
                    label="Gender"
                  >
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </CustomSelect>
                  {validationModel.genderError}
                </div>
                <div className="col-span-12 md:col-span-6">
                  <CustomSelect
                    onChange={handleChange}
                    name="title"
                    value={ptModel.title}
                    label={
                      <>
                        Identity (Optional) <span className="text-gray-500">Pronouns</span>
                      </>
                    }
                    toolTip="We understand that gender is a spectrum and that you may not identify as either male or female. Some billing, credentialing and licensing applications still require us to enter one of these two options, however, we have provided an optional Pronoun field that we'll use in our correspondence with you."
                    tooltipWidth={500}
                  >
                    {IDENTITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </CustomSelect>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6">
                  <CustomSelect
                    onChange={handleChange}
                    name="maritalStatus"
                    value={ptModel.maritalStatus}
                    label="Marital Status"
                  >
                    {MARITAL_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </CustomSelect>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <CustomSelect
                    onChange={handleChange}
                    name="GuarantorRelation"
                    value={ptModel.GuarantorRelation || ""}
                    label="Guarantor"
                    toolTip="The person responsible for paying the medical bills"
                  >
                    {relationship.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.display}
                      </option>
                    ))}
                  </CustomSelect>
                </div>
              </div>
            </>
          )}
        </div>
      </SuperAccordion>
    </motion.div>
  );
};

Identification.propTypes = {
  ptModel: PropTypes.object.isRequired,
  validationModel: PropTypes.object.isRequired,
  showOnlyRequired: PropTypes.bool.isRequired,
  sectionsOpen: PropTypes.object.isRequired,
  onToggleSection: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default Identification;

